/**
 * Caregiver DashboardPage (CONTRACTS §6, spec §9.3): linked clients with a
 * due/missed medication summary per client, recent intake events, proofs
 * pending review and unread notifications. Every client section fails
 * independently; all scoping is repository-side.
 */
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, EmptyState, ErrorState, Skeleton, StatusChip } from '@/components/ui'
import { BellIcon, UserIcon, UsersIcon, VideoIcon } from '@/components/icons'
import { PATHS } from '@/app/router/paths'
import { formatDutchDateTime, occurrenceStatusMeta, todayLocalISODate } from '@/features/medication'
import { useAuth } from '@/features/auth'
import { useCaregiverDashboardData } from './useLinkedClients'
import './caregiver.css'

function displayName(name: string | null): string {
  return name?.trim() ? name : 'Cliënt'
}

function initials(name: string | null): string {
  const clean = displayName(name)
  return clean
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const today = todayLocalISODate()
  const { status, error, clients, summaries, recentEvents, pendingProofs, unreadNotifications, refresh } =
    useCaregiverDashboardData(today)

  return (
    <div className="caregiver-page">
      <header>
        <h1 className="caregiver-section-title">Overzicht</h1>
        <p className="caregiver-muted">
          Welkom{profile?.displayName ? `, ${profile.displayName}` : ''}. Hier zie je je gekoppelde
          cliënten en hun medicatie van vandaag.
        </p>
      </header>

      {status === 'loading' ? <Skeleton lines={4} /> : null}
      {status === 'unavailable' ? (
        <Card padding="md">
          <p className="caregiver-muted">
            Cloud niet ingesteld. Het overzicht kan nu niet worden opgehaald.
          </p>
        </Card>
      ) : null}
      {status === 'error' ? (
        <ErrorState error={error ?? undefined} onRetry={() => void refresh()} />
      ) : null}

      {status === 'ready' ? (
        <>
          {/* Notifications */}
          <section className="caregiver-section" aria-labelledby="cg-notifications">
            <div className="caregiver-section-header">
              <h2 id="cg-notifications" className="caregiver-section-title">
                <BellIcon size={18} /> Meldingen
                {unreadNotifications > 0 ? (
                  <span className="caregiver-badge" aria-label={`${unreadNotifications} ongelezen`}>
                    {unreadNotifications}
                  </span>
                ) : null}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate(PATHS.caregiver.notifications)}>
                Alles bekijken
              </Button>
            </div>
            {unreadNotifications === 0 ? (
              <p className="caregiver-muted">Geen ongelezen meldingen.</p>
            ) : (
              <p className="caregiver-muted">
                {unreadNotifications === 1
                  ? '1 ongelezen melding.'
                  : `${unreadNotifications} ongelezen meldingen.`}
              </p>
            )}
          </section>

          {/* Linked clients with due/missed summary */}
          <section className="caregiver-section" aria-labelledby="cg-clients">
            <div className="caregiver-section-header">
              <h2 id="cg-clients" className="caregiver-section-title">
                <UsersIcon size={18} /> Gekoppelde cliënten
              </h2>
              <Button variant="ghost" size="sm" onClick={() => navigate(PATHS.caregiver.clients)}>
                Beheren
              </Button>
            </div>
            {clients.length === 0 ? (
              <EmptyState
                icon={<UsersIcon size={26} />}
                title="Nog geen cliënten gekoppeld"
                body="Nodig een cliënt uit met het e-mailadres waarmee die is geregistreerd."
                action={
                  <Button variant="primary" onClick={() => navigate(PATHS.caregiver.clients)}>
                    Cliënt uitnodigen
                  </Button>
                }
              />
            ) : (
              <ul className="caregiver-list">
                {clients.map((client) => {
                  const summary = summaries.get(client.id)
                  return (
                    <li key={client.id}>
                      <Card padding="md" className="caregiver-client-card" onClick={() => navigate(PATHS.caregiver.clientDetail(client.id))}>
                        <div className="caregiver-client-header">
                          <span className="caregiver-client-avatar" aria-hidden="true">
                            {initials(client.displayName)}
                          </span>
                          <span className="caregiver-client-name">{displayName(client.displayName)}</span>
                        </div>
                        {summary?.failed ? (
                          <p className="caregiver-muted">Gegevens van deze cliënt konden niet laden.</p>
                        ) : (
                          <div className="caregiver-chip-row">
                            <StatusChip status={summary && summary.dueCount > 0 ? 'info' : 'neutral'}>
                              {summary?.dueCount ?? 0} nog te innemen
                            </StatusChip>
                            <StatusChip status={summary && summary.missedCount > 0 ? 'warning' : 'neutral'}>
                              {summary?.missedCount ?? 0} gemist
                            </StatusChip>
                            <StatusChip status="success">{summary?.takenCount ?? 0} ingenomen</StatusChip>
                            {summary && summary.pendingProofCount > 0 ? (
                              <StatusChip status="info">
                                {summary.pendingProofCount} bewijs te bekijken
                              </StatusChip>
                            ) : null}
                          </div>
                        )}
                      </Card>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {/* Proofs pending review */}
          <section className="caregiver-section" aria-labelledby="cg-proofs">
            <h2 id="cg-proofs" className="caregiver-section-title">
              <VideoIcon size={18} /> Bewijsvideo’s te bekijken
            </h2>
            {pendingProofs.length === 0 ? (
              <p className="caregiver-muted">Er wachten geen bewijsvideo’s op jou.</p>
            ) : (
              <Card padding="sm">
                <ul className="caregiver-list">
                  {pendingProofs.map(({ event, client }) => (
                    <li key={event.id} className="caregiver-event-item">
                      <span className="caregiver-event-text">
                        <span className="caregiver-event-title">
                          {displayName(client?.displayName ?? null)}
                        </span>
                        <span className="caregiver-event-meta">
                          Geüpload op {formatDutchDateTime(event.proofUploadedAt ?? event.updatedAt)}
                        </span>
                      </span>
                      {client ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(PATHS.caregiver.clientProofs(client.id))}
                        >
                          Bekijken
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </section>

          {/* Recent intake events */}
          <section className="caregiver-section" aria-labelledby="cg-events">
            <h2 id="cg-events" className="caregiver-section-title">
              <UserIcon size={18} /> Recente innames
            </h2>
            {recentEvents.length === 0 ? (
              <p className="caregiver-muted">Nog geen geregistreerde innames.</p>
            ) : (
              <Card padding="sm">
                <ul className="caregiver-list">
                  {recentEvents.map(({ event, client }) => {
                    const meta = occurrenceStatusMeta(event.status)
                    return (
                      <li key={event.id} className="caregiver-event-item">
                        <span className="caregiver-event-text">
                          <span className="caregiver-event-title">
                            {displayName(client?.displayName ?? null)}
                          </span>
                          <span className="caregiver-event-meta">
                            {event.takenAt
                              ? `Ingenomen op ${formatDutchDateTime(event.takenAt)}`
                              : `Bijgewerkt op ${formatDutchDateTime(event.updatedAt)}`}
                          </span>
                        </span>
                        <StatusChip status={meta.chip}>{meta.label}</StatusChip>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            )}
          </section>
        </>
      ) : null}

      <p className="caregiver-muted">
        Je ziet alleen gegevens van actief gekoppelde cliënten. Alles wat je bekijkt en wijzigt
        wordt vastgelegd in het auditlog.{' '}
        <Link to={PATHS.caregiver.clients}>Beheer koppelingen</Link>
      </p>
    </div>
  )
}
